import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { changePassword, createBillingCheckout, updateProfile } from "../services/authService";
import ApiKeysCard from "../components/ApiKeysCard";

function ProfileSettingsPage() {
  const { user, reload } = useAuth();
  const location = useLocation();

  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    currentPassword: "",
    inviteCode: "",
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [sectionsOpen, setSectionsOpen] = useState({
    email: false,
    password: false,
    invite: false,
  });

  const [status, setStatus] = useState("");
  const [inviteStatus, setInviteStatus] = useState("");
  const [passwordStatus, setPasswordStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [billingStatus, setBillingStatus] = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState("");
  const billingSearchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      name: user?.name || "",
      email: user?.email || "",
    }));
  }, [user?.name, user?.email]);

  useEffect(() => {
    const billing = billingSearchParams.get("billing");
    const reason = billingSearchParams.get("reason");
    if (billing === "cancel") {
      setBillingStatus(reason ? `Platba byla zrušena: ${reason}` : "Platba byla zrušena.");
    }
    if (billing === "failed") {
      setBillingStatus(reason ? `Platba neproběhla: ${reason}` : "Platba neproběhla. Zkuste to prosím znovu.");
    }
  }, [billingSearchParams]);

  const toggleSection = (name) => {
    setSectionsOpen((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handlePasswordFieldChange = (field) => (event) => {
    setPasswordForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleBasicProfileSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setStatus("");
    try {
      await updateProfile({ name: form.name });
      await reload();
      setStatus("Základní údaje byly uloženy.");
    } catch (err) {
      setStatus(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEmailChangeSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setStatus("");

    const emailChanged = form.email.trim().toLowerCase() !== (user?.email || "").toLowerCase();
    if (!emailChanged) {
      setStatus("E-mail se nezměnil.");
      setSaving(false);
      return;
    }
    if (!form.currentPassword) {
      setStatus("Pro změnu e-mailu zadejte aktuální heslo.");
      setSaving(false);
      return;
    }

    try {
      await updateProfile({
        email: form.email,
        currentPassword: form.currentPassword,
      });
      await reload();
      setStatus("E-mail byl úspěšně změněn.");
      setForm((prev) => ({ ...prev, currentPassword: "" }));
      setSectionsOpen((prev) => ({ ...prev, email: false }));
    } catch (err) {
      setStatus(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleInviteSubmit = async (event) => {
    event.preventDefault();
    setInviting(true);
    setInviteStatus("");
    try {
      await updateProfile({ inviteCode: form.inviteCode });
      await reload();
      setInviteStatus("Pozvánkový kód byl aktivován.");
      setForm((prev) => ({ ...prev, inviteCode: "" }));
      setSectionsOpen((prev) => ({ ...prev, invite: false }));
    } catch (err) {
      setInviteStatus(err.message);
    } finally {
      setInviting(false);
    }
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();
    setChangingPassword(true);
    setPasswordStatus("");

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordStatus("Nové heslo a potvrzení hesla se neshodují.");
      setChangingPassword(false);
      return;
    }

    try {
      await changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordStatus("Heslo bylo úspěšně změněno.");
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setSectionsOpen((prev) => ({ ...prev, password: false }));
    } catch (err) {
      setPasswordStatus(err.message);
    } finally {
      setChangingPassword(false);
    }
  };

  const startCheckout = async (productType) => {
    setBillingStatus("");
    setCheckoutLoading(productType);
    try {
      const result = await createBillingCheckout({ productType });
      if (!result?.url) {
        throw new Error("Stripe checkout URL is missing");
      }
      window.location.href = result.url;
    } catch (err) {
      setBillingStatus(err.message || "Nepodařilo se otevřít platbu.");
    } finally {
      setCheckoutLoading("");
    }
  };

  return (
    <>
      <section className="app-card grid-duble">

                        <div className="">
        <h3>Nastavení profilu</h3>
        <form className="profile-form" onSubmit={handleBasicProfileSubmit}>
          <label>
            Jméno
            <input type="text" value={form.name} onChange={handleChange("name")} />
          </label>
          <label>
            Aktuální e-mail
            <input type="email" value={user?.email || ""} disabled />
          </label>
          <label>
            Aktuální heslo (pouze pro změnu e-mailu)
            <input type="password" value={form.currentPassword} onChange={handleChange("currentPassword")} />
          </label>
          <div className="profile-actions">
            <button className="site-button settings-toggle-btn" type="submit" disabled={saving}>
              {saving ? "Ukládám..." : "Uložit jméno"}
            </button>
                      <button className="site-button settings-toggle-btn" type="button" onClick={() => toggleSection("email")}>
            {sectionsOpen.email ? "Skrýt změnu e-mailu" : "Změnit e-mail"}
          </button>
          <button className="site-button settings-toggle-btn" type="button" onClick={() => toggleSection("password")}>
            {sectionsOpen.password ? "Skrýt změnu hesla" : "Změnit heslo"}
          </button>

            <button className="site-button settings-toggle-btn" type="button" onClick={() => toggleSection("invite")}>
              {sectionsOpen.invite ? "Skrýt pozvánkový kód" : "Pozvánkový kód"}
            </button>
          </div>
          {status && <span className="auth-modal__hint">{status}</span>}
        </form>






        {sectionsOpen.email && (
          <form className="profile-form settings-expanded-form" onSubmit={handleEmailChangeSubmit}>
            <h3>Změna e-mailu</h3>
            <label>
              Nový e-mail
              <input type="email" value={form.email} onChange={handleChange("email")} />
            </label>
            <label>
              Potvrzení aktuálním heslem
              <input type="password" value={form.currentPassword} onChange={handleChange("currentPassword")} />
            </label>
            <button className="site-button site-button--primary" type="submit" disabled={saving}>
              {saving ? "Měním..." : "Potvrdit změnu e-mailu"}
            </button>
          </form>
        )}

        {sectionsOpen.password && (
          <form className="profile-form settings-expanded-form" onSubmit={handleChangePassword}>
            <h3>Změna hesla</h3>
            <label>
              Aktuální heslo
              <input type="password" value={passwordForm.currentPassword} onChange={handlePasswordFieldChange("currentPassword")} />
            </label>
            <label>
              Nové heslo
              <input type="password" value={passwordForm.newPassword} onChange={handlePasswordFieldChange("newPassword")} minLength={8} />
            </label>
            <label>
              Potvrďte nové heslo
              <input type="password" value={passwordForm.confirmPassword} onChange={handlePasswordFieldChange("confirmPassword")} minLength={8} />
            </label>
            <button className="site-button site-button--primary" type="submit" disabled={changingPassword}>
              {changingPassword ? "Měním heslo..." : "Potvrdit změnu hesla"}
            </button>
            {passwordStatus && <span className="auth-modal__hint">{passwordStatus}</span>}
          </form>
        )}

        {sectionsOpen.invite && (
          <form className="profile-form settings-expanded-form" onSubmit={handleInviteSubmit}>
            <h3>Pozvánkový kód</h3>
            <label>
              Zadejte pozvánkový kód
              <input type="text" value={form.inviteCode} onChange={handleChange("inviteCode")} />
            </label>
            <button className="site-button site-button--primary" type="submit" disabled={inviting}>
              {inviting ? "Aktivuji..." : "Aktivovat kód"}
            </button>
            {inviteStatus && <span className="auth-modal__hint">{inviteStatus}</span>}
          </form>
        )}
</div>
                <div className="">
        <h3>Kredity a využití služby</h3>
        <div className="credits-overview-grid">
          <div className="credits-metric">
            <span className="credits-metric__label">Dostupné kredity</span>
            <strong className="credits-metric__value">{user?.credits ?? 0}</strong>
          </div>
          <div className="credits-metric">
            <span className="credits-metric__label">Aktivní tarif</span>
            <strong className="credits-metric__value credits-metric__value--small">
              {user?.subscription_plan === "monthly_200" ? "Legacy tarif (historický účet)" : "Bez předplatného"}
            </strong>
          </div>
        </div>

        <div className="credits-rules-list">
          <p><strong>Start:</strong> při registraci získáváte 20 kreditů zdarma. K první platbě připíšeme bonus 100 kreditů.</p>
          <p><strong>Spotřeba:</strong> analýza firmy = 2 kredity, analýza výkazů PDF = 5 kreditů, zpráva chatbotu = 1 kredit (+1 kredit, když odpověď počítá firmu nebo čerpá z databáze firem a oborů).</p>
          <p><strong>Důležité:</strong> výstupy mají informativní charakter a nenahrazují odborné poradenství.</p>
        </div>

        </div>
      </section>



      <section className="app-card app-card--full-width">
        <h3>Dobíjení kreditů (jednorázová platba)</h3>

        <div className="subscription-grid">
          <div className="subscription-card">
            <strong>Bezpečná platba kartou (Stripe)</strong>
            <span>Visa, Mastercard, Apple Pay a Google Pay s ověřením 3D Secure. Platba proběhne na zabezpečené stránce Stripe.</span>
          </div>
          <div className="subscription-card">
            <strong>Dobíjení 20 kreditů</strong>
            <span>100 Kč včetně DPH (jednorázová platba).</span>
            <button
              className="site-button site-button--primary"
              type="button"
              disabled={checkoutLoading === "one_time_20"}
              onClick={() => startCheckout("one_time_20")}
            >
              {checkoutLoading === "one_time_20" ? "Přesměrování..." : "Koupit 20 kreditů"}
            </button>
          </div>
          <div className="subscription-card">
            <strong>Dobíjení 40 kreditů</strong>
            <span>200 Kč včetně DPH (jednorázová platba).</span>
            <button
              className="site-button site-button--primary"
              type="button"
              disabled={checkoutLoading === "one_time_40"}
              onClick={() => startCheckout("one_time_40")}
            >
              {checkoutLoading === "one_time_40" ? "Přesměrování..." : "Koupit 40 kreditů"}
            </button>
          </div>
          <div className="subscription-card">
            <strong>Dobíjení 100 kreditů</strong>
            <span>500 Kč včetně DPH (jednorázová platba).</span>
            <button
              className="site-button site-button--primary"
              type="button"
              disabled={checkoutLoading === "one_time_100"}
              onClick={() => startCheckout("one_time_100")}
            >
              {checkoutLoading === "one_time_100" ? "Přesměrování..." : "Koupit 100 kreditů"}
            </button>
          </div>
        </div>

        <p className="auth-modal__hint">Aktuálně nabízíme pouze jednorázový nákup kreditů bez automatického obnovování a bez závazného předplatného.</p>
        <p className="auth-modal__hint">Platby zpracovává platební brána Stripe (Stripe Payments Europe, Ltd.). Údaje o platební kartě zadáváte přímo u Stripe, FinPilot k nim nemá přístup.</p>
        <p className="auth-modal__hint">Měna transakcí: CZK. Ceny uvedené výše jsou konečné včetně DPH, pokud je dle právních předpisů účtována.</p>
        <p className="auth-modal__hint">Nákupní proces je dostupný pouze ze schválené a registrované domény provozovatele a při přechodu do platební brány probíhá zabezpečeně přes HTTPS.</p>
        <p className="auth-modal__hint">
          Před dokončením objednávky se seznamte s podmínkami služby a ochranou osobních údajů: <Link to="/vop">VOP</Link>
          {" · "}
          <Link to="/gdpr">GDPR</Link>
        </p>
        <p className="auth-modal__hint">Poskytovatel služby: Ing. Ivan Bazhin, IČO 19556489, Děčínská 552/1, Praha 8 – Střížkov, 180 00, Česká republika. Není platcem DPH</p>
        <p className="auth-modal__hint">Kontakt: info@finpilot.cz, tel. +420 774 592 936.</p>
        <p className="auth-modal__hint">Informace o odstoupení od smlouvy, reklamacích a mimosoudním řešení spotřebitelských sporů naleznete ve VOP.</p>
        {billingStatus && <p className="auth-modal__hint">{billingStatus}</p>}
      </section>
      <ApiKeysCard />
    </>
  );
}

export default ProfileSettingsPage;
